import React from 'react';
import type { StaticContext } from '@react-router';
import { NoSsr } from '@shared/components/NoSsr';
import { GuestSiteHead } from './GuestSiteHead';
import { LazyGuestSiteBody } from './GuestSiteBody.lazy';

export interface GuestSiteProps
  extends Readonly<{
    eventHandle: string;
    staticContext: StaticContext | undefined;
  }> {}

/**
 * Main GuestSite component optimized for SSR performance.
 *
 * Architecture:
 * - GuestSiteHead: Renders server-side for SEO/meta tags (minimal query)
 * - GuestSiteBody: Renders client-side only (full query + all features)
 *
 * Benefits:
 * - Fast TTFB: Only lightweight head data fetched during SSR
 * - Optimized bundle: Body chunk loaded only on client
 * - Better SEO: Meta tags always present in initial HTML
 */
const GuestSite: React.FC<GuestSiteProps> = ({ eventHandle, staticContext }) => {
  return (
    <>
      {/* Always render Head for SEO - even during SSR */}
      <GuestSiteHead eventHandle={eventHandle} />

      {/* Body is client-only to minimize SSR payload and initial bundle size */}
      <NoSsr>
        <LazyGuestSiteBody
          eventHandle={eventHandle}
          staticContext={staticContext}
        />
      </NoSsr>
    </>
  );
};

GuestSite.displayName = 'GuestSite';

export { GuestSite };
